import { useEffect, useState, useRef } from 'react';

function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
}

export default function TrustProof() {
  const stats = [
    { value: 500, suffix: '+', label: '導入台数' },
    { value: 98, suffix: '%', label: '継続利用率' },
    { value: 12, suffix: '社', label: '法人導入実績' },
  ];

  return (
    <section id="case-studies" className="relative py-28 overflow-hidden">
      {/* Dynamic Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]"></div>
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-teal-500/10 to-emerald-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
            <span className="text-amber-400 text-sm font-medium tracking-wide">TRUSTED BY LEADERS</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            導入実績
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            厳しい運用環境で選ばれ続ける、MeVERの信頼性
          </p>
        </div>

        {/* Stats Counter Bar */}
        <div className="flex flex-wrap justify-center gap-8 md:gap-16 mb-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="text-5xl md:text-6xl font-bold text-white mb-2 transition-transform group-hover:scale-110">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-white/50 text-sm tracking-wider uppercase">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Case Study Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* CASE 01 Card */}
          <div className="group">
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 hover:shadow-amber-500/20 hover:-translate-y-2 h-full flex flex-col">
              {/* Accent Corner */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-400 to-orange-500 opacity-10 rounded-br-full z-10"></div>
              
              {/* Image Section */}
              <div className="relative h-72 flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/55959ea32bc2e8e3156d251d01b97f0d.jpeg"
                  alt="在日米軍基地"
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <span className="bg-amber-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg">Pro メイン</span>
                  <span className="bg-teal-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg">Style 併用</span>
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                    <i className="ri-government-fill text-amber-600 text-xl"></i>
                  </span>
                  <span className="text-amber-600 text-sm font-bold tracking-wider">CASE 01</span>
                </div>
                
                <h3 className="text-2xl font-bold text-[#1a1a2e] mb-4 leading-tight">
                  在日米軍基地に採用
                </h3>
                
                <p className="text-[#4a4a4a] leading-relaxed mb-6 flex-grow">
                  高い耐久性と長時間使用が求められる在日米軍基地にて、<span className="font-bold text-[#1a1a2e]">MeVER Chair Pro</span> をメインに採用。会議室や健康志向ユーザー向けには <span className="font-bold text-[#1a1a2e]">MeVER Chair Style</span> も併用され、用途別に最適化された運用が行われています。
                </p>
                
                <div className="flex items-center gap-4 pt-4 border-t border-gray-100 mt-auto">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    US
                  </div>
                  <div>
                    <div className="font-bold text-[#1a1a2e]">在日米軍施設</div>
                    <div className="text-sm text-gray-500">オフィス・会議室</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CASE 02 Card */}
          <div className="group">
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 hover:shadow-teal-500/20 hover:-translate-y-2 h-full flex flex-col">
              {/* Accent Corner */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-teal-400 to-emerald-500 opacity-10 rounded-br-full z-10"></div>
              
              {/* Image Section */}
              <div className="relative h-72 flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/4efedb369b060dd7be72a84f55edcb4a.webp"
                  alt="コワーキングスペース"
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <span className="bg-teal-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg">Style メイン</span>
                  <span className="bg-amber-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg">Pro 併用</span>
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl">
                    <i className="ri-building-4-fill text-teal-600 text-xl"></i>
                  </span>
                  <span className="text-teal-600 text-sm font-bold tracking-wider">CASE 02</span>
                </div>
                
                <h3 className="text-2xl font-bold text-[#1a1a2e] mb-4 leading-tight">
                  コワーキングスペース by favy 新宿
                </h3>
                
                <p className="text-[#4a4a4a] leading-relaxed mb-6 flex-grow">
                  多様な職種・働き方が混在する環境で、<span className="font-bold text-[#1a1a2e]">MeVER Chair Style</span> を中心に導入。集中ブースには <span className="font-bold text-[#1a1a2e]">MeVER Chair Pro</span> を配置し、利用者の目的に応じた最適な座り心地を提供しています。
                </p>
                
                <div className="flex items-center gap-4 pt-4 border-t border-gray-100 mt-auto">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    <i className="ri-building-4-fill text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-[#1a1a2e]">favy 新宿</div>
                    <div className="text-sm text-gray-500">コワーキングスペース</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Card - Full Width */}
        <div className="mt-8 group">
          <div className="relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 hover:shadow-amber-500/40 hover:-translate-y-2 p-8 md:p-10">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-building-2-fill text-white/80 text-xl"></i>
                  <span className="text-white/80 text-sm font-medium">法人のお客様へ</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  導入事例・お見積もりのご相談はこちら
                </h3>
                
                <p className="text-white/80 text-sm">
                  御社の課題に合わせた最適なプランをご提案いたします
                </p>
              </div>
              
              <a
                href="#b2b"
                className="inline-flex items-center gap-2 bg-white text-amber-600 px-8 py-4 rounded-full font-bold text-base hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
              >
                <span>法人お問い合わせ</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Trust Badges */}
        <div className="mt-16 flex flex-wrap justify-center items-center gap-8 opacity-60">
          <div className="flex items-center gap-2 text-white/70">
            <i className="ri-shield-check-fill text-2xl"></i>
            <span className="text-sm">品質保証</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <i className="ri-customer-service-2-fill text-2xl"></i>
            <span className="text-sm">専任サポート</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <i className="ri-truck-fill text-2xl"></i>
            <span className="text-sm">全国配送対応</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <i className="ri-tools-fill text-2xl"></i>
            <span className="text-sm">設置サービス</span>
          </div>
        </div>
      </div>
    </section>
  );
}
